/**
 * Route-level error boundary.
 *
 * Catches render-time errors below this subtree and shows a friendly fallback
 * with a retry button. Reset is triggered on location change (via `locationKey`
 * prop) so navigating away and back clears the error.
 */

import { Component, type ErrorInfo, type ReactNode } from 'react';
import { AlertTriangle, RotateCcw } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';

interface ErrorBoundaryProps {
  children: ReactNode;
  /** Pass `location.pathname` so resetting on navigation is automatic. */
  locationKey?: string;
}

interface ErrorBoundaryState {
  error: Error | null;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  override state: ErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error };
  }

  override componentDidUpdate(prev: Readonly<ErrorBoundaryProps>): void {
    if (prev.locationKey !== this.props.locationKey && this.state.error) {
      this.setState({ error: null });
    }
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error('ErrorBoundary caught render error', error, info);
  }

  private readonly handleRetry = () => {
    this.setState({ error: null });
  };

  override render(): ReactNode {
    if (!this.state.error) {
      return this.props.children;
    }
    return (
      <div className="flex min-h-[60vh] items-center justify-center p-6">
        <Card className="w-full max-w-lg border-destructive/40">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-destructive">
              <AlertTriangle className="size-5" />
              Algo deu errado
            </CardTitle>
            <CardDescription>
              Um erro inesperado ocorreu enquanto esta tela era renderizada.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <pre className="max-h-48 overflow-auto rounded-md bg-muted p-3 text-xs">
              {this.state.error.message}
            </pre>
            <Button onClick={this.handleRetry}>
              <RotateCcw className="size-4" /> Tentar novamente
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }
}
